function formatProductMoney(cents, section) {
  const numericCents = Number(cents);
  if (!Number.isFinite(numericCents)) return '';

  const currency = section?.dataset.productCurrency || 'EUR';
  const locale = section?.dataset.productLocale || document.documentElement.lang || 'es-ES';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(numericCents / 100);
  } catch (error) {
    return `${(numericCents / 100).toFixed(2).replace('.', ',')} €`;
  }
}

function renderVariantPrice(priceElement, variant, section) {
  if (!priceElement || !variant) return;

  const price = Number(variant.price);
  const compareAtPrice = Number(variant.compare_at_price || 0);
  if (!Number.isFinite(price)) return;

  const onSale = compareAtPrice > price;
  priceElement.classList.toggle('price--sale', onSale);
  priceElement.classList.toggle('price--sold-out', !variant.available);
  priceElement.replaceChildren();

  const current = document.createElement('span');
  current.className = 'price__current';
  current.textContent = formatProductMoney(price, section);
  priceElement.append(current);

  if (onSale) {
    const compare = document.createElement('s');
    compare.className = 'price__compare';
    compare.textContent = formatProductMoney(compareAtPrice, section);
    priceElement.append(compare);

    const badge = document.createElement('span');
    badge.className = 'price__badge';
    badge.textContent = `-${Math.round(((compareAtPrice - price) * 100) / compareAtPrice)} %`;
    priceElement.append(badge);
  }
}

function updateProductVariantUI(section, variant) {
  if (!section || !variant) return;

  section.querySelectorAll('[data-product-price]').forEach((priceElement) => {
    renderVariantPrice(priceElement, variant, section);
  });

  section.querySelectorAll('[data-main-add-to-cart], [data-sticky-add-to-cart]').forEach((button) => {
    const available = Boolean(variant.available);
    button.disabled = !available;
    button.dataset.variantAvailable = available ? 'true' : 'false';
    button.setAttribute('aria-disabled', available ? 'false' : 'true');
    button.textContent = available ? 'Añadir al carrito' : 'Agotado';
  });

  const limitedStatus = section.querySelector('[data-limited-status]');
  if (limitedStatus) limitedStatus.hidden = !variant.available;
}

class VariantRadios extends HTMLElement {
  connectedCallback() {
    if (this.dataset.variantRadiosReady === 'true') return;

    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    try {
      this.variants = JSON.parse(script.textContent);
    } catch (error) {
      this.variants = [];
    }

    this.variantChangeHandler = this.onVariantChange.bind(this);
    this.addEventListener('change', this.variantChangeHandler);
    this.dataset.variantRadiosReady = 'true';
  }

  disconnectedCallback() {
    if (this.variantChangeHandler) {
      this.removeEventListener('change', this.variantChangeHandler);
    }
    delete this.dataset.variantRadiosReady;
  }

  onVariantChange() {
    const selectedOptions = Array.from(this.querySelectorAll('input:checked')).map((input) => input.value);
    const variant = this.variants.find((item) => {
      return item.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) return;

    const form = this.closest('form');
    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    const section = form.closest('[data-product-section]');

    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = !variant.available;
      submitButton.dataset.variantAvailable = variant.available ? 'true' : 'false';
      submitButton.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
      submitButton.textContent = variant.available ? 'Añadir al carrito' : 'Agotado';
    }

    updateProductVariantUI(section, variant);

    if (form.hasAttribute('data-main-product-form') && this.dataset.url && window.history?.replaceState) {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    document.dispatchEvent(new CustomEvent('luvia:variant-change', {
      detail: { variant, form, section }
    }));
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('[data-header]');
  const updateHeaderShadow = () => {
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  };

  updateHeaderShadow();
  window.addEventListener('scroll', updateHeaderShadow, { passive: true });

  document.querySelectorAll('[data-announcement-rotating]').forEach((bar) => {
    const items = Array.from(bar.querySelectorAll('[data-announcement-item]'));
    if (items.length < 2) return;

    let index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    const interval = Number(bar.dataset.slideInterval || 3000);

    window.setInterval(() => {
      items[index].classList.remove('is-active');
      items[index].setAttribute('aria-hidden', 'true');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
      items[index].removeAttribute('aria-hidden');
    }, interval);
  });

  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchInput = searchOverlay ? searchOverlay.querySelector('input[type="search"]') : null;
  const desktopSearch = document.querySelector('[data-desktop-search]');
  const desktopSearchInput = desktopSearch ? desktopSearch.querySelector('input[type="search"]') : null;
  const desktopSearchMedia = window.matchMedia('(min-width: 1081px)');
  const openSearchButtons = document.querySelectorAll('[data-search-open]');
  const closeSearchButtons = document.querySelectorAll('[data-search-close]');

  const setSearchButtonsExpanded = (expanded) => {
    openSearchButtons.forEach((button) => button.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
  };

  const openDesktopSearch = () => {
    if (!desktopSearch) return;
    desktopSearch.hidden = false;
    header?.classList.add('is-search-open');
    setSearchButtonsExpanded(true);
    window.setTimeout(() => desktopSearchInput?.focus(), 40);
  };

  const closeDesktopSearch = () => {
    if (!desktopSearch) return;
    desktopSearch.hidden = true;
    header?.classList.remove('is-search-open');
    setSearchButtonsExpanded(false);
  };

  const openMobileSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.hidden = false;
    document.body.style.overflow = 'hidden';
    setSearchButtonsExpanded(true);
    window.setTimeout(() => searchInput?.focus(), 40);
  };

  const closeMobileSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.hidden = true;
    document.body.style.overflow = '';
    setSearchButtonsExpanded(false);
  };

  const openSearch = () => {
    if (desktopSearchMedia.matches) {
      if (desktopSearch && !desktopSearch.hidden) closeDesktopSearch();
      else openDesktopSearch();
      return;
    }
    openMobileSearch();
  };

  openSearchButtons.forEach((button) => button.addEventListener('click', openSearch));
  closeSearchButtons.forEach((button) => button.addEventListener('click', closeMobileSearch));

  document.addEventListener('click', (event) => {
    if (!desktopSearchMedia.matches || !desktopSearch || desktopSearch.hidden) return;
    if (desktopSearch.contains(event.target) || event.target.closest('[data-search-open]')) return;
    closeDesktopSearch();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (desktopSearch && !desktopSearch.hidden) closeDesktopSearch();
    if (searchOverlay && !searchOverlay.hidden) closeMobileSearch();
  });

  desktopSearchMedia.addEventListener?.('change', () => {
    closeDesktopSearch();
    closeMobileSearch();
  });

  const menu = document.querySelector('[data-mobile-menu]');
  const openButton = document.querySelector('[data-mobile-menu-open]');
  const closeButton = document.querySelector('[data-mobile-menu-close]');

  if (menu && openButton && closeButton) {
    const openMobileMenu = () => {
      menu.hidden = false;
      menu.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mobile-menu-open');
    };

    const closeMobileMenu = () => {
      menu.hidden = true;
      menu.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mobile-menu-open');
    };

    openButton.addEventListener('click', () => {
      if (menu.hidden) openMobileMenu();
      else closeMobileMenu();
    });

    closeButton.addEventListener('click', closeMobileMenu);

    menu.addEventListener('click', (event) => {
      if (event.target === menu || event.target.closest('a')) {
        closeMobileMenu();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 1080 && !menu.hidden) closeMobileMenu();
    });
  }

  document.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = button.parentElement?.querySelector('input');
      if (!input) return;

      const minimum = Number(input.min || 1);
      const current = Number(input.value || minimum);
      const next = button.hasAttribute('data-qty-plus') ? current + 1 : Math.max(minimum, current - 1);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-filter-form]').forEach((form) => {
    form.addEventListener('change', () => form.submit());
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();

      const submit = form.querySelector('button[type="submit"]');
      const previousText = submit?.textContent?.trim() || 'Añadir al carrito';

      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = 'Añadiendo...';
      }

      try {
        const addResponse = await fetch(window.themeRoutes.cartAdd, {
          method: 'POST',
          body: new FormData(form),
          headers: {
            Accept: 'application/json'
          }
        });

        if (!addResponse.ok) throw new Error('Cart add failed');

        const cartResponse = await fetch(`${window.themeRoutes.cartUrl}.js`);
        if (!cartResponse.ok) throw new Error('Cart refresh failed');

        const cart = await cartResponse.json();
        document.querySelectorAll('[data-cart-count]').forEach((node) => {
          node.textContent = cart.item_count;
        });

        const toast = document.querySelector('[data-cart-toast]');
        if (toast) {
          toast.hidden = false;
          window.setTimeout(() => {
            toast.hidden = true;
          }, 3600);
        }
      } catch (error) {
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        if (submit) {
          const available = submit.dataset.variantAvailable !== 'false';
          submit.disabled = !available;
          submit.removeAttribute('aria-busy');
          submit.textContent = available ? previousText : 'Agotado';
        }
      }
    });
  });
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-filter-drawer-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const drawer = document.querySelector('[data-filter-drawer]');
      if (!drawer) return;
      drawer.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => drawer.querySelector('[data-filter-drawer-close]')?.focus());
    });
  });

  document.querySelectorAll('[data-filter-drawer-close]').forEach((button) => {
    button.addEventListener('click', () => {
      const drawer = button.closest('[data-filter-drawer]') || document.querySelector('[data-filter-drawer]');
      if (!drawer) return;
      drawer.hidden = true;
      document.querySelector('[data-filter-drawer-open]')?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.querySelector('[data-filter-drawer-open]')?.focus();
    });
  });

  document.querySelectorAll('[data-quick-view-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const modal = document.getElementById(button.dataset.quickViewOpen);
      if (!modal) return;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    });
  });

  document.querySelectorAll('[data-quick-view-close]').forEach((button) => {
    button.addEventListener('click', () => {
      const modal = button.closest('[data-quick-view-modal]');
      if (!modal) return;
      modal.hidden = true;
      document.body.style.overflow = '';
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-quick-view-modal]:not([hidden]), [data-filter-drawer]:not([hidden])').forEach((element) => {
      element.hidden = true;
      if (element.matches('[data-filter-drawer]')) {
        document.querySelector('[data-filter-drawer-open]')?.setAttribute('aria-expanded', 'false');
      }
      document.body.style.overflow = '';
    });
  });

  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const stickyBar = section.querySelector('[data-sticky-product-bar]');
    const mainAddToCart = section.querySelector('[data-main-add-to-cart]');
    const stickyAddToCart = section.querySelector('[data-sticky-add-to-cart]');

    if (!stickyBar || !mainAddToCart) return;

    const syncStickyState = () => {
      if (!stickyAddToCart) return;
      stickyAddToCart.disabled = mainAddToCart.disabled;
      stickyAddToCart.dataset.variantAvailable = mainAddToCart.dataset.variantAvailable || 'true';
      stickyAddToCart.setAttribute('aria-disabled', mainAddToCart.disabled ? 'true' : 'false');
      stickyAddToCart.textContent = mainAddToCart.textContent.trim();
    };

    const updateStickyBar = () => {
      const rect = mainAddToCart.getBoundingClientRect();
      stickyBar.hidden = rect.bottom > 0;
    };

    syncStickyState();
    updateStickyBar();
    window.addEventListener('scroll', updateStickyBar, { passive: true });
    window.addEventListener('resize', updateStickyBar);

    const buttonObserver = new MutationObserver(syncStickyState);
    buttonObserver.observe(mainAddToCart, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['disabled', 'data-variant-available', 'aria-busy']
    });

    stickyAddToCart?.addEventListener('click', () => {
      if (!stickyAddToCart.disabled) mainAddToCart.click();
    });
  });
});


function getMadridCalendarDate() {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Madrid',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());

    const values = {};
    parts.forEach((part) => {
      if (part.type !== 'literal') values[part.type] = part.value;
    });

    return new Date(Date.UTC(
      Number(values.year),
      Number(values.month) - 1,
      Number(values.day),
      12
    ));
  } catch (error) {
    const now = new Date();
    return new Date(Date.UTC(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      12
    ));
  }
}

function getNextDispatchDate() {
  const currentDate = getMadridCalendarDate();
  const weekday = currentDate.getUTCDay();
  let daysToAdd = 1;

  if (weekday === 5) daysToAdd = 3;
  if (weekday === 6) daysToAdd = 2;
  if (weekday === 0) daysToAdd = 1;

  const dispatchDate = new Date(currentDate);
  dispatchDate.setUTCDate(dispatchDate.getUTCDate() + daysToAdd);
  return dispatchDate;
}

function formatDispatchDate(date) {
  const weekdays = ['dom.', 'lun.', 'mar.', 'mié.', 'jue.', 'vie.', 'sáb.'];
  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  return `Enviamos el ${weekdays[date.getUTCDay()]} ${date.getUTCDate()} de ${months[date.getUTCMonth()]}`;
}

function updateDispatchDates() {
  const label = formatDispatchDate(getNextDispatchDate());
  document.querySelectorAll('[data-dispatch-date]').forEach((element) => {
    element.textContent = label;
  });
}

document.addEventListener('DOMContentLoaded', updateDispatchDates);
document.addEventListener('shopify:section:load', updateDispatchDates);
window.setInterval(updateDispatchDates, 60 * 60 * 1000);


/* v5.5 product gallery: capped desktop thumbnails + mobile swipe carousel + variant sync */
document.addEventListener('DOMContentLoaded', () => {
  const mobileGalleryMedia = window.matchMedia('(max-width: 989px)');

  const parseGalleryVariants = (gallery) => {
    if (!gallery) return [];
    const json = gallery.querySelector('[data-gallery-variants]')?.textContent;
    if (!json) return [];

    try {
      return JSON.parse(json);
    } catch (error) {
      return [];
    }
  };

  const getGalleryItems = (gallery) => Array.from(gallery?.querySelectorAll('[data-gallery-item]') || []);

  const updateGalleryCounter = (gallery, index) => {
    const counter = gallery?.querySelector('[data-gallery-counter]');
    const total = getGalleryItems(gallery).length;
    if (counter && total) counter.textContent = `${index + 1}/${total}`;
  };

  const applyGalleryIndex = (gallery, index, options = {}) => {
    if (!gallery) return false;
    const items = getGalleryItems(gallery);
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const stage = gallery.querySelector('[data-gallery-stage]');
    const normalizedIndex = Math.max(0, Math.min(Number(index) || 0, items.length - 1));
    const targetItem = items[normalizedIndex];
    if (!targetItem) return false;

    items.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === normalizedIndex));
    thumbs.forEach((thumb) => {
      const isMoreThumb = thumb.hasAttribute('data-gallery-more');
      const thumbIndex = Number(thumb.dataset.mediaIndex || 0);
      const active = isMoreThumb ? normalizedIndex >= thumbIndex : normalizedIndex === thumbIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    gallery.dataset.activeMediaIndex = String(normalizedIndex);
    updateGalleryCounter(gallery, normalizedIndex);

    if (mobileGalleryMedia.matches && options.scroll !== false && stage) {
      stage.scrollTo({
        left: targetItem.offsetLeft,
        behavior: options.instant ? 'auto' : 'smooth'
      });
    }

    return true;
  };

  const activateGalleryMedia = (gallery, mediaId, imageId, mediaIndex, options = {}) => {
    if (!gallery) return false;
    const items = getGalleryItems(gallery);
    let targetIndex = -1;

    if (mediaId) {
      targetIndex = items.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
    }
    if (targetIndex < 0 && imageId) {
      targetIndex = items.findIndex((item) => String(item.dataset.imageId) === String(imageId));
    }
    if (targetIndex < 0 && mediaIndex !== undefined && mediaIndex !== null) {
      targetIndex = items.findIndex((item) => String(item.dataset.mediaIndex) === String(mediaIndex));
    }
    if (targetIndex < 0) return false;

    return applyGalleryIndex(gallery, targetIndex, options);
  };

  const syncGalleryToVariantId = (form, variantId) => {
    if (!form || !variantId) return;
    const section = form.closest('[data-product-section]');
    const gallery = section?.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const variants = parseGalleryVariants(gallery);
    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    activateGalleryMedia(gallery, variant.media_id, variant.image_id);
  };

  const getVariantIdFromForm = (form) => {
    if (!form) return null;
    const inputs = Array.from(form.querySelectorAll('input[name="id"]'));
    const currentInput = inputs.find((input) => input.value) || inputs[0];
    return currentInput?.value || null;
  };

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const items = getGalleryItems(gallery);
    const stage = gallery.querySelector('[data-gallery-stage]');
    const initialIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    applyGalleryIndex(gallery, initialIndex, { instant: true });

    gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        applyGalleryIndex(gallery, Number(thumb.dataset.mediaIndex || 0));
      });
    });

    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
      const current = Number(gallery.dataset.activeMediaIndex || 0);
      applyGalleryIndex(gallery, (current - 1 + items.length) % items.length);
    });

    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
      const current = Number(gallery.dataset.activeMediaIndex || 0);
      applyGalleryIndex(gallery, (current + 1) % items.length);
    });

    if (stage) {
      let scrollTimer = null;
      stage.addEventListener('scroll', () => {
        if (!mobileGalleryMedia.matches) return;
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const stageRect = stage.getBoundingClientRect();
          let nearestIndex = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;
          items.forEach((item, index) => {
            const distance = Math.abs(item.getBoundingClientRect().left - stageRect.left);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
            }
          });
          applyGalleryIndex(gallery, nearestIndex, { scroll: false });
        }, 70);
      }, { passive: true });
    }
  });

  document.addEventListener('luvia:variant-change', (event) => {
    const variant = event.detail?.variant;
    const section = event.detail?.section;
    const mediaId = variant?.featured_media?.id || variant?.media_id;
    const imageId = variant?.featured_image?.id || variant?.image?.id || variant?.image_id;
    const galleries = section
      ? section.querySelectorAll('[data-product-gallery]')
      : document.querySelectorAll('[data-product-gallery]');

    galleries.forEach((gallery) => {
      activateGalleryMedia(gallery, mediaId, imageId);
    });
  });

  document.querySelectorAll('[data-main-product-form]').forEach((form) => {
    let lastVariantId = null;
    let syncTimer = null;

    const sync = () => {
      const variantId = getVariantIdFromForm(form);
      if (!variantId || variantId === lastVariantId) return;
      lastVariantId = variantId;
      syncGalleryToVariantId(form, variantId);
    };

    const scheduleSync = () => {
      window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(sync, 40);
      window.setTimeout(sync, 140);
      window.setTimeout(sync, 320);
    };

    form.addEventListener('change', scheduleSync);
    form.addEventListener('input', scheduleSync);
    form.addEventListener('click', scheduleSync);

    const observer = new MutationObserver(scheduleSync);
    observer.observe(form, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['value', 'checked', 'data-variant-id']
    });

    sync();
  });

  mobileGalleryMedia.addEventListener?.('change', () => {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const index = Number(gallery.dataset.activeMediaIndex || 0);
      window.setTimeout(() => applyGalleryIndex(gallery, index, { instant: true }), 30);
    });
  });


  document.querySelectorAll('[data-product-description-link]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const selector = link.getAttribute('href');
      const details = selector ? document.querySelector(selector) : null;
      if (!details) return;
      event.preventDefault();
      details.open = true;
      details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', selector);
    });
  });

  window.addEventListener('popstate', () => {
    document.querySelectorAll('[data-main-product-form]').forEach((form) => {
      window.setTimeout(() => {
        const variantId = getVariantIdFromForm(form);
        syncGalleryToVariantId(form, variantId);
      }, 40);
    });
  });
});

/* v8.8 — reliable Shopify collection filter submission */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-collection-filter-form]').forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.filterSubmitReady === 'true') return;
    form.dataset.filterSubmitReady = 'true';

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      form.querySelectorAll('input[type="number"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.value = input.value.trim().replace(',', '.');
      });

      const params = new URLSearchParams();
      const formData = new FormData(form);
      for (const [name, rawValue] of formData.entries()) {
        const value = String(rawValue).trim();
        if (!value || name === 'page') continue;
        params.append(name, value);
      }

      const target = new URL(form.action || window.location.pathname, window.location.origin);
      target.search = params.toString();
      window.location.assign(target.toString());
    });
  });
});

/* v9.6 product page: sticky image gallery interactions, fullscreen viewer and hover magnifier */
document.addEventListener('DOMContentLoaded', () => {
  const desktopGalleryMedia = window.matchMedia('(min-width: 990px)');
  const desktopHoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const lightbox = document.querySelector('[data-luvia-gallery-lightbox]');

  let lightboxItems = [];
  let lightboxIndex = 0;
  let lightboxOpener = null;

  const normalizeIndex = (index, length) => {
    if (!length) return 0;
    return ((Number(index) || 0) % length + length) % length;
  };

  const getGalleryItemsForLightbox = (gallery) => Array.from(
    gallery?.querySelectorAll('[data-gallery-item][data-lightbox-src]') || []
  ).filter((item) => item.dataset.lightboxSrc);

  const renderLightboxThumbnails = () => {
    if (!lightbox) return;
    const container = lightbox.querySelector('[data-luvia-lightbox-thumbs]');
    if (!container) return;

    const fragment = document.createDocumentFragment();
    lightboxItems.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'luvia-gallery-lightbox__thumb';
      button.dataset.luviaLightboxThumb = String(index);
      button.setAttribute('aria-label', `Ver imagen ${index + 1} de ${lightboxItems.length}`);

      const thumbnail = document.createElement('img');
      thumbnail.src = item.dataset.lightboxThumb || item.querySelector('img')?.currentSrc || item.dataset.lightboxSrc;
      thumbnail.alt = '';
      thumbnail.loading = 'lazy';
      thumbnail.decoding = 'async';
      button.appendChild(thumbnail);
      fragment.appendChild(button);
    });

    container.replaceChildren(fragment);
    container.hidden = lightboxItems.length <= 1;
  };

  const updateLightbox = (index) => {
    if (!lightbox || !lightboxItems.length) return;
    lightboxIndex = normalizeIndex(index, lightboxItems.length);
    const item = lightboxItems[lightboxIndex];
    const image = lightbox.querySelector('[data-luvia-lightbox-image]');
    const counter = lightbox.querySelector('[data-luvia-lightbox-counter]');
    const previous = lightbox.querySelector('[data-luvia-lightbox-prev]');
    const next = lightbox.querySelector('[data-luvia-lightbox-next]');

    if (image) {
      image.src = item.dataset.lightboxSrc;
      image.alt = item.dataset.lightboxAlt || item.querySelector('img')?.alt || '';
    }
    if (counter) counter.textContent = `${lightboxIndex + 1}/${lightboxItems.length}`;

    const hasMultiple = lightboxItems.length > 1;
    if (previous) previous.hidden = !hasMultiple;
    if (next) next.hidden = !hasMultiple;
    if (counter) counter.hidden = !hasMultiple;

    lightbox.querySelectorAll('[data-luvia-lightbox-thumb]').forEach((thumbnail) => {
      const active = Number(thumbnail.dataset.luviaLightboxThumb) === lightboxIndex;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      if (active && lightbox.open) thumbnail.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  const openLightboxFromItem = (item) => {
    if (!lightbox || !desktopGalleryMedia.matches || !item) return;
    const gallery = item.closest('[data-product-gallery]');
    if (!gallery) return;

    lightboxItems = getGalleryItemsForLightbox(gallery);
    if (!lightboxItems.length) return;

    const itemIndex = lightboxItems.indexOf(item);
    lightboxOpener = item;
    renderLightboxThumbnails();
    updateLightbox(itemIndex >= 0 ? itemIndex : 0);
    document.documentElement.classList.add('is-gallery-lightbox-open');
    document.body.classList.add('is-gallery-lightbox-open');

    if (typeof lightbox.showModal === 'function') {
      if (!lightbox.open) lightbox.showModal();
    } else {
      lightbox.setAttribute('open', '');
    }

    window.requestAnimationFrame(() => {
      lightbox.querySelector('[data-luvia-lightbox-close]')?.focus({ preventScroll: true });
    });
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    if (typeof lightbox.close === 'function' && lightbox.open) {
      lightbox.close();
    } else {
      lightbox.removeAttribute('open');
      lightbox.dispatchEvent(new Event('close'));
    }
  };

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-luvia-lightbox-close]');
    if (closeButton) {
      event.preventDefault();
      closeLightbox();
      return;
    }

    const previous = event.target.closest('[data-luvia-lightbox-prev]');
    if (previous) {
      event.preventDefault();
      updateLightbox(lightboxIndex - 1);
      return;
    }

    const next = event.target.closest('[data-luvia-lightbox-next]');
    if (next) {
      event.preventDefault();
      updateLightbox(lightboxIndex + 1);
      return;
    }

    const thumbnail = event.target.closest('[data-luvia-lightbox-thumb]');
    if (thumbnail) {
      event.preventDefault();
      updateLightbox(Number(thumbnail.dataset.luviaLightboxThumb));
      return;
    }

    const galleryItem = event.target.closest('[data-gallery-item][data-lightbox-src]');
    if (galleryItem && desktopGalleryMedia.matches) {
      event.preventDefault();
      openLightboxFromItem(galleryItem);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (lightbox?.open) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        updateLightbox(lightboxIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        updateLightbox(lightboxIndex + 1);
      }
      return;
    }

    if (!desktopGalleryMedia.matches || !['Enter', ' '].includes(event.key)) return;
    const galleryItem = event.target.closest?.('[data-gallery-item][data-lightbox-src]');
    if (!galleryItem) return;
    event.preventDefault();
    openLightboxFromItem(galleryItem);
  });

  lightbox?.addEventListener('click', (event) => {
    if (event.target === lightbox) closeLightbox();
  });

  lightbox?.addEventListener('close', () => {
    document.documentElement.classList.remove('is-gallery-lightbox-open');
    document.body.classList.remove('is-gallery-lightbox-open');
    const opener = lightboxOpener;
    lightboxOpener = null;
    lightboxItems = [];
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });

  const hideLens = (item) => {
    if (!item) return;
    item.classList.remove('is-zooming');
  };

  const updateLens = (item, event) => {
    if (!desktopHoverMedia.matches || !item?.classList.contains('is-active')) return;
    const lens = item.querySelector('[data-gallery-zoom-lens]');
    const image = item.querySelector('img');
    if (!lens || !image || !image.complete) return;

    const rect = item.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    if (localX < 0 || localY < 0 || localX > rect.width || localY > rect.height) {
      hideLens(item);
      return;
    }

    const naturalWidth = image.naturalWidth || rect.width;
    const naturalHeight = image.naturalHeight || rect.height;
    const objectFit = window.getComputedStyle(image).objectFit || 'cover';
    const baseScale = objectFit === 'contain'
      ? Math.min(rect.width / naturalWidth, rect.height / naturalHeight)
      : Math.max(rect.width / naturalWidth, rect.height / naturalHeight);
    const renderedWidth = naturalWidth * baseScale;
    const renderedHeight = naturalHeight * baseScale;
    const offsetX = (rect.width - renderedWidth) / 2;
    const offsetY = (rect.height - renderedHeight) / 2;

    if (
      objectFit === 'contain'
      && (localX < offsetX || localX > offsetX + renderedWidth || localY < offsetY || localY > offsetY + renderedHeight)
    ) {
      hideLens(item);
      return;
    }

    const lensSize = lens.offsetWidth || 158;
    const halfLens = lensSize / 2;
    const lensX = Math.max(halfLens, Math.min(localX, rect.width - halfLens));
    const lensY = Math.max(halfLens, Math.min(localY, rect.height - halfLens));
    const zoom = 2.45;
    const source = item.dataset.lightboxSrc || image.currentSrc || image.src;
    const safeSource = String(source).replace(/"/g, '%22');

    lens.style.left = `${lensX}px`;
    lens.style.top = `${lensY}px`;
    lens.style.backgroundImage = `url("${safeSource}")`;
    lens.style.backgroundSize = `${renderedWidth * zoom}px ${renderedHeight * zoom}px`;
    lens.style.backgroundPosition = `${halfLens - (localX - offsetX) * zoom}px ${halfLens - (localY - offsetY) * zoom}px`;
    item.classList.add('is-zooming');
  };

  document.addEventListener('pointermove', (event) => {
    if (!desktopHoverMedia.matches || event.pointerType === 'touch') return;
    const item = event.target.closest?.('[data-gallery-item][data-lightbox-src]');
    if (!item) return;
    updateLens(item, event);
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    const item = event.target.closest?.('[data-gallery-item][data-lightbox-src]');
    if (!item || item.contains(event.relatedTarget)) return;
    hideLens(item);
  });

  desktopHoverMedia.addEventListener?.('change', (event) => {
    if (event.matches) return;
    document.querySelectorAll('.product-gallery__item.is-zooming').forEach(hideLens);
  });
});
